import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Zap, Crosshair, BookOpen, Library, Trophy, Lock } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export const err = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;

export const TYPE_ICON = { quick: Zap, topic: Crosshair, chapter: BookOpen, subject: Library, mock: Trophy };
export const TYPE_COLOR = {
  quick: "text-amber-300 bg-amber-400/10 border-amber-400/20", topic: "text-cyan-300 bg-cyan-400/10 border-cyan-400/20",
  chapter: "text-indigo-300 bg-indigo-400/10 border-indigo-400/20", subject: "text-emerald-300 bg-emerald-400/10 border-emerald-400/20",
  mock: "text-rose-300 bg-rose-400/10 border-rose-400/20",
};
export const TYPE_LABEL = { quick: "Quick Test", topic: "Topic Test", chapter: "Chapter Test", subject: "Subject Test", mock: "Full Mock Test" };

export const PATTERN_STYLE = {
  official_2027: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300",
  latest_official: "border-cyan-400/30 bg-cyan-400/10 text-cyan-300",
  practice_assumption: "border-amber-400/30 bg-amber-400/10 text-amber-300",
};
export const PATTERN_LABEL = {
  official_2027: "Official 2027 pattern", latest_official: "Latest official pattern · 2027 not yet notified", practice_assumption: "Practice pattern · not an official exam pattern",
};

export const trackEvent = (type, meta = {}) => api.post("/test-prep/events", { type, meta }).catch(() => {});

export const fmtTime = (sec) => {
  const s = Math.max(0, Math.floor(sec || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  return h ? `${h}:${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}` : `${m}:${String(r).padStart(2, "0")}`;
};

export async function startTest(navigate, testId, fromRecommendation = false, source = null) {
  try {
    const { data } = await api.post("/test-prep/attempts", { test_id: testId, from_recommendation: fromRecommendation, source });
    navigate(`/prep/test/${data.id}`);
  } catch (e) {
    toast.error(err(e));
  }
}

export const TypeBadge = ({ type }) => {
  const Icon = TYPE_ICON[type] || Zap;
  return (
    <span data-testid={`type-badge-${type}`} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${TYPE_COLOR[type] || TYPE_COLOR.quick}`}>
      <Icon className="h-3 w-3" /> {TYPE_LABEL[type] || type}
    </span>
  );
};

export const PatternBadge = ({ status }) => status ? (
  <span data-testid="pattern-status-badge" className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold ${PATTERN_STYLE[status] || PATTERN_STYLE.practice_assumption}`}>
    {PATTERN_LABEL[status] || status}
  </span>
) : null;

export const Stat = ({ label, value, sub, testid, tone = "cyan" }) => (
  <div data-testid={testid} className="rounded-2xl border border-white/8 bg-[#131C2E] p-4">
    <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
    <p className={`mt-1 font-mono-data text-2xl font-extrabold text-${tone}-300`}>{value}</p>
    {sub && <p className="mt-0.5 text-xs text-slate-500">{sub}</p>}
  </div>
);

export const SampleNote = () => (
  <div data-testid="sample-content-note" className="rounded-xl border border-amber-400/20 bg-amber-400/5 px-4 py-2.5 text-xs text-amber-300">
    Sample practice questions — created to validate the EntranceZone test engine. They are not official, previous-year or verified exam questions.
  </div>
);

export const LockedPill = () => (
  <span className="inline-flex items-center gap-1 rounded-full bg-amber-400/15 px-2 py-0.5 text-[11px] font-semibold text-amber-300"><Lock className="h-3 w-3" /> Premium</span>
);

export const Crumb = ({ items }) => (
  <nav className="mb-4 flex flex-wrap items-center gap-1 text-xs text-slate-500">
    {items.map((it, i) => (
      <span key={i} className="flex items-center gap-1">
        {i > 0 && <span>/</span>}
        {it.to ? <Link to={it.to} className="hover:text-cyan-300">{it.label}</Link> : <span className="text-slate-300">{it.label}</span>}
      </span>
    ))}
  </nav>
);
